import React, { useMemo } from 'react'
import { Col, Form, Row } from 'antd'
import PropTypes from 'prop-types'

import {
  FormButtons,
  FormCheckbox,
  FormDate,
  FormInput,
  FormSelect,
  FormTreeSelect,
} from '../../../common/components/sections'
import {
  StyledPageActions,
  StyledPageHeader,
  StyledPageTitle,
} from '../../../common/components/styled'
import { PERMISSIONS } from '../../../common/constants'
import { useFormDirty } from '../../../common/hooks'
import { optionPropType, treeOptionPropType } from '../../../common/prop-types'

import { userDetailsPropType } from '../../prop-types'

import { userEditorFormInitialValues } from './initial-values'

const UserEditorForm = ({
  user,
  roleOptions,
  cuisineTypeOptions,
  languageOptions,
  onSubmit,
}) => {
  const [form] = Form.useForm()
  const { isDirty, setToDirty, resetForm } = useFormDirty(form)
  const initialValues = useMemo(() => userEditorFormInitialValues(user), [user])

  return (
    <Form
      form={form}
      initialValues={initialValues}
      name="editUser"
      onFinish={onSubmit}
      autoComplete="off"
      onFieldsChange={setToDirty}
    >
      <StyledPageHeader>
        <StyledPageTitle>{user ? 'Edit user' : 'Create user'}</StyledPageTitle>
        <StyledPageActions>
          <FormButtons
            permissions={[PERMISSIONS.USER.UPDATE, PERMISSIONS.USER.CREATE]}
            isDisabled={!isDirty}
            onReset={resetForm}
          />
        </StyledPageActions>
      </StyledPageHeader>
      <Row gutter={[16]}>
        <Col span={5}>
          <FormInput
            name="name"
            label="Name"
            labelAbove
            placeholder="Input name..."
            rules={[
              {
                required: true,
              },
            ]}
          />
        </Col>
        <Col span={4}>
          <FormSelect
            name="roleIds"
            label="Role"
            labelAbove
            showSearch
            options={roleOptions}
            mode="multiple"
            placeholder="Select roles..."
            rules={[
              {
                required: true,
              },
            ]}
          />
        </Col>
        <Col span={5}>
          <FormInput
            name="email"
            label="Email"
            labelAbove
            placeholder="Input email..."
            rules={[
              {
                required: true,
                type: 'email',
              },
            ]}
          />
        </Col>
        <Col span={4}>
          <FormTreeSelect
            label="Cuisine knowledge"
            labelAbove
            showSearch
            allowClear
            multiple
            name="cuisineTypeIds"
            treeData={cuisineTypeOptions}
            maxSelectableOptions={20}
            placeholder="Select Cuisine Types..."
          />
        </Col>
        <Col span={4}>
          <FormSelect
            label="Languages"
            labelAbove
            allowClear
            showSearch
            placeholder="Select Languages..."
            mode="multiple"
            name="languageIds"
            maxSelectableOptions={20}
            options={languageOptions}
          />
        </Col>
      </Row>
      <Row>
        <Col span={2}>
          <FormCheckbox name="isActive" label="Enable user" />
        </Col>
        {user && (
          <Col span={3}>
            <FormDate
              name="lastLoginAt"
              label="Login date"
              labelAbove
              getFieldValue={form.getFieldValue}
            />
          </Col>
        )}
      </Row>
    </Form>
  )
}

UserEditorForm.propTypes = {
  user: userDetailsPropType,
  roleOptions: PropTypes.arrayOf(optionPropType),
  cuisineTypeOptions: PropTypes.arrayOf(treeOptionPropType),
  languageOptions: PropTypes.arrayOf(optionPropType),
  onSubmit: PropTypes.func.isRequired,
}

UserEditorForm.defaultProps = {
  user: undefined,
  roleOptions: [],
  cuisineTypeOptions: [],
  languageOptions: [],
}

export default UserEditorForm
